import React, { useState, useEffect, useRef } from 'react';
import { holeToken } from '../../lib/sitzung';
import { MIcon } from '../../lib/micon';
import heic2any from 'heic2any';
import { LoadingBlock } from '../../components/ui/LoadingSpinner';
import { API_BASE, apiHeaders, apiPost, apiFetch, apiGet, leseFallback, zeigeApiFehler } from '../../lib/api';
import { useIsMobile, eindeutigeKlassen } from '../../lib/hooks';
import { useSchueler } from '../../contexts/SchuelerContext';
import { useKlassen } from '../../contexts/KlassenContext';
import { useFab } from '../../contexts/FabContext';
import { toast } from '../../lib/toast';
import { KlassenbuchEintrag, KlasseFachEintrag } from '../../types';
import { LdSheet } from '../../components/ui/LdSheet';
import '../schueler/U6Bearbeiten.css';
import { mikrofonAnfragen, zeigeMikrofonFehler } from '../../lib/mikrofon';
import { KlassenKopf, PortalMenu } from '../klasse/KlassenKopf';
import { globalNav, nimmKlassenbuchZiel, KlassenbuchZiel, ProfilHerkunft, oeffneKlassenbuchNachtragen, KLASSENBUCH_GEAENDERT } from '../../lib/nav';
import { useZeitraster, fmtZeit } from '../../lib/zeitraster';
import { getAktuellerNutzer } from '../../lib/aktuellerNutzer';
import { heuteIso, tm, wtKommaTm } from '../schueler/profil/format';
import { datumTTMMJJJJ } from '../../lib/datum';
import { StundeEintragen, SpeichernArt } from './StundeEintragen';
import { kalenderwoche } from './MitarbeitImEintrag';
import '../klasse/klasse.css';
import './klassenbuch-k2.css';
import { LdDatum, LdKalender, LdFensterFuss } from '../../components/ld/LdFenster';

// ── Datum-Helfer (Kalendertage, ohne Zeitzonen-Verschiebung) ──
const plusTage = (iso: string, n: number) => { const x = new Date(iso + 'T12:00:00Z'); x.setUTCDate(x.getUTCDate() + n); return x.toISOString().slice(0, 10); };
const wtNr = (iso: string) => { const w = new Date(iso + 'T12:00:00Z').getUTCDay(); return w === 0 ? 7 : w; };   // Mo=1 … So=7
const montagVon = (iso: string) => plusTage(iso, 1 - wtNr(iso));
const WT_LANG = ['', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const tagTitel = (iso: string) => `${WT_LANG[wtNr(iso)]}, ${tm(iso)}`;
type Status = 'eingetragen' | 'offen' | 'kommend';
type Offen = { eintraege: { datum: string; stunde_nr: number; klasse_id: number; fach: string }[]; gesamt: number };

// Welle K2: Mitarbeit einer eingetragenen Stunde („6 Kinder“) — Werte der Woche im Fach (GET /api/klassenbuch/mitarbeit)
function MitarbeitAnzahl({ klasseId, fach, datum }: { klasseId: number; fach: string; datum: string }) {
  const [n, setN] = useState<number | null>(null);
  useEffect(() => {
    let aktiv = true;
    apiGet<{ erlaubt: boolean; werte: Record<string, unknown> }>(`/api/klassenbuch/mitarbeit?klasse_id=${klasseId}&fach=${encodeURIComponent(fach)}&datum=${datum}`)
      .then(d => { if (aktiv) setN(d.erlaubt ? Object.keys(d.werte ?? {}).length : null); }).catch(() => {});
    return () => { aktiv = false; };
  }, [klasseId, fach, datum]);
  if (n == null) return null;
  return <><dt>Mitarbeit</dt><dd>{n === 0 ? 'noch keine' : `${n} ${n === 1 ? 'Kind' : 'Kinder'}`}</dd></>;
}

// Welle U8: vorgabeKlasseId gesetzt = eingebettet im Klassen-Screen (Unterricht) — die Klasse
// oben gilt, eigene Klassen-Knöpfe, Auto-Fill und Sprungziele entfallen. Ohne wie bisher.
// Welle K2: Handy = ein Tag (Karten je eigener Stunde, offene Stunden der Woche, Per Sprache nachtragen);
// PC = die Woche links, die gewählte Stunde rechts direkt bearbeitbar. Daten und Abläufe unverändert.
export function KlassenbuchScreen({ active = true, vorgabeKlasseId }: { active?: boolean; vorgabeKlasseId?: number | null } = {}) {
  const eingebettet = vorgabeKlasseId !== undefined;
  const { schuelerListe, setOpenProfilId } = useSchueler();
  const isMobile = useIsMobile();
  const { meineKlassen } = useKlassen();
  const { zeiten } = useZeitraster();

  const heute = heuteIso();
  const monatVon = heute.slice(0, 8) + '01';
  const [datum,          setDatum]          = useState(heute);
  const [aktiveKlasse,   setAktiveKlasse]   = useState<KlasseFachEintrag | null>(null);
  const [woche,          setWoche]          = useState<Record<string, KlassenbuchEintrag[]>>({});
  const [offeneStunde,   setOffeneStunde]   = useState<number | null>(null);
  const [laden,          setLaden]          = useState(false);
  const [exportVon,      setExportVon]      = useState(monatVon);
  const [exportBis,      setExportBis]      = useState(heute);
  const [exportLaden,    setExportLaden]    = useState(false);
  const [exportOffen,    setExportOffen]    = useState(false);
  const exportKnopf = useRef<HTMLButtonElement>(null);
  const [autoFillGeladen, setAutoFillGeladen] = useState(false);
  const [kbDiktatPhase,    setKbDiktatPhase]    = useState<'idle' | 'aufnahme' | 'verarbeitung'>('idle');
  const [kbVorausgefuellt, setKbVorausgefuellt] = useState<{ thema?: string; hausaufgaben?: string; anmerkungen?: string; abwesende_schueler?: string[] } | null>(null);
  const kbDiktatRecorderRef = useRef<MediaRecorder | null>(null);
  const kbStreamRef         = useRef<Promise<MediaStream> | null>(null);
  const [offen, setOffen] = useState<Offen | null>(null);
  const [sucheOffen, setSucheOffen] = useState(false);
  const datumKnopf = useRef<HTMLButtonElement>(null);
  const [kalenderOffen, setKalenderOffen] = useState(false);

  type FotoStunde = {
    stunde: number; fach: string | null; thema: string | null;
    hausaufgaben: string | null; anmerkungen: string | null;
    fehlende_schueler: number[]; uebernehmen: boolean;
  };
  const [showFotoModal,  setShowFotoModal]  = useState(false);
  const [fotoLaden,      setFotoLaden]      = useState(false);
  const [fotoFehler,     setFotoFehler]     = useState<string | null>(null);
  const [fotoVorschau,   setFotoVorschau]   = useState<FotoStunde[] | null>(null);
  const [suchanfrage,    setSuchanfrage]    = useState('');
  const [suchTreffer,    setSuchTreffer]    = useState<any[]>([]);
  const [suchLaden,      setSuchLaden]      = useState(false);
  const [stundenplan,    setStundenplan]    = useState<{ wochentag: number; stunde: number; fach: string; klasse_id: number | null; created_at?: string | null }[]>([]);

  // Welle U6b: Sprung "Klassenbuch an einem Datum öffnen" (Fehlzeit-Sheet, Suche, Schnell-Diktat).
  // Das Ziel wird beim Einhängen abgeholt; zielAktivRef hält den Auto-Fill (aktuelle Stunde) fern.
  const [kbZiel, setKbZiel] = useState<KlassenbuchZiel | null>(null);
  const [kbHerkunft, setKbHerkunft] = useState<ProfilHerkunft | null>(null);
  const zielAktivRef = useRef(false);
  const zielStundeRef = useRef<number | null>(null);          // erst öffnen, wenn die Einträge geladen sind
  const [geladenFuer, setGeladenFuer] = useState<string>('');  // "klasse_id|datum" der zuletzt geladenen Einträge
  useEffect(() => {
    if (eingebettet) return; // Sprungziele gehören dem eigenständigen Klassenbuch
    const z = nimmKlassenbuchZiel();
    if (!z) return;
    zielAktivRef.current = true;
    setAutoFillGeladen(true);
    setKbHerkunft(z.herkunft ?? null);
    setDatum(z.datum);
    setKbZiel(z);
  // eingebettet ist je Instanz fest (eigenständig: nie gesetzt); wechselt es im Klassen-Screen von
  // undefined auf eine Klasse, kehrt der Effekt sofort zurück — das Sprungziel wird nicht doppelt abgeholt
  }, [eingebettet]);
  function zurueckZurHerkunft() {
    if (!kbHerkunft) return;
    setOpenProfilId(kbHerkunft.profilId, kbHerkunft.ziel);
    globalNav('schueler');
  }

  // Aktive Klasse aus Kontext initialisieren
  useEffect(() => {
    if (eingebettet) return;
    if (meineKlassen.length > 0 && !aktiveKlasse) {
      setAktiveKlasse(eindeutigeKlassen(meineKlassen)[0]);   // Welle B3: wie die Chips (Klassenleitung vorn)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meineKlassen]);

  // Welle U8: eingebettet der Klasse oben folgen; offener Eintrag/Foto/Export schließen
  useEffect(() => {
    if (!eingebettet) return;
    const k = meineKlassen.find(x => Number(x.klasse_id) === Number(vorgabeKlasseId)) ?? null;
    setAktiveKlasse(prev => (Number(prev?.klasse_id) === Number(k?.klasse_id) ? prev : k));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eingebettet, vorgabeKlasseId, meineKlassen]);
  const vorigeVorgabe = useRef(vorgabeKlasseId);
  useEffect(() => {
    if (vorigeVorgabe.current === vorgabeKlasseId) return;
    vorigeVorgabe.current = vorgabeKlasseId;
    setOffeneStunde(null); setKbVorausgefuellt(null); setShowFotoModal(false); setFotoVorschau(null); setExportOffen(false);
  }, [vorgabeKlasseId]);

  function exportKlassenbuch() {
    if (!aktiveKlasse) return;
    setExportLaden(true);
    const url = `${API_BASE}/api/export/klassenbuch/${aktiveKlasse.klasse_id}?von=${exportVon}&bis=${exportBis}`;
    fetch(url, { headers: apiHeaders() })
      .then(r => r.ok ? r.blob() : Promise.reject('Fehler'))
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `Klassenbuch_${aktiveKlasse.klasse_name}_${exportVon}_${exportBis}.pdf`;
        a.click();
      })
      .catch(() => toast('Export fehlgeschlagen', 'error'))
      .finally(() => { setExportLaden(false); setExportOffen(false); });
  }

  // Auto-Fill: Stundenplan-Eintrag für die aktuelle Stunde laden
  useEffect(() => {
    if (eingebettet || autoFillGeladen || datum !== heute || zielAktivRef.current) return;
    fetch(`${API_BASE}/api/stundenplan/jetzt`, { headers: apiHeaders() })
      .then(leseFallback<any>(null, '/api/stundenplan/jetzt'))
      .then(sp => {
        if (!sp || zielAktivRef.current) return;
        setAutoFillGeladen(true);
        if (sp.klasse_id && meineKlassen.length > 0) {
          const passendeKlasse = meineKlassen.find(k =>
            k.klasse_id === sp.klasse_id && k.fach === sp.fach
          ) || meineKlassen.find(k => k.klasse_id === sp.klasse_id);
          if (passendeKlasse) setAktiveKlasse(passendeKlasse);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meineKlassen, datum]);

  // Welle K2: offene Stunden — dieselbe Quelle wie „Jetzt nachtragen“ auf Heute
  function ladeOffen() {
    apiFetch<Offen>('/api/heute/klassenbuch-offen').then(setOffen).catch(() => setOffen(null));
  }

  // Welle U6b: nur die jüngste Anfrage zählt (beim Sprung laufen Heute- und Ziel-Datum parallel)
  // Welle K2: geladen wird die ganze Woche des Datums (Mo–Fr, dazu das Datum selbst) über GET /api/klassenbuch je Tag
  const ladeNrRef = useRef(0);
  function reloadEintraege(k = aktiveKlasse, d = datum) {
    if (!k) return;
    const nr = ++ladeNrRef.current;
    setLaden(true);
    const tage = Array.from(new Set([0, 1, 2, 3, 4].map(i => plusTage(montagVon(d), i)).concat(d)));
    Promise.all(tage.map(t => fetch(`${API_BASE}/api/klassenbuch?klasse_id=${k.klasse_id}&datum=${t}`, { headers: apiHeaders() })
      .then(leseFallback<KlassenbuchEintrag[]>([], '/api/klassenbuch')).catch(() => [] as KlassenbuchEintrag[])))
      .then(listen => {
        if (nr !== ladeNrRef.current) return;
        const m: Record<string, KlassenbuchEintrag[]> = {};
        tage.forEach((t, i) => { m[t] = Array.isArray(listen[i]) ? listen[i] : []; });
        setWoche(m); setGeladenFuer(`${k.klasse_id}|${d}`);
      })
      .finally(() => { if (nr === ladeNrRef.current) setLaden(false); });
    ladeOffen();
  }

  // Einträge laden wenn Datum oder Klasse wechselt
  useEffect(() => {
    reloadEintraege();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datum, aktiveKlasse]);
  // Welle B3: nach „Per Sprache nachtragen“ neu laden
  const reloadRef = useRef(reloadEintraege);
  reloadRef.current = reloadEintraege;
  useEffect(() => {
    const h = () => reloadRef.current();
    window.addEventListener(KLASSENBUCH_GEAENDERT, h);
    return () => window.removeEventListener(KLASSENBUCH_GEAENDERT, h);
  }, []);

  // Vollen Stundenplan einmal laden (für Fach-Vorschlag je Datum+Stunde)
  useEffect(() => {
    fetch(`${API_BASE}/api/stundenplan`, { headers: apiHeaders() })
      .then(leseFallback<any[]>([], '/api/stundenplan')).then((d: any[]) => setStundenplan(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  async function fotoHochladen(file: File) {
    if (!aktiveKlasse) return;
    setFotoLaden(true);
    setFotoFehler(null);
    try {
      let dataUrl: string;
      if (file.type === 'image/heic' || file.type === 'image/heif' || file.name.toLowerCase().endsWith('.heic')) {
        const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.85 });
        const blob = Array.isArray(converted) ? converted[0] : converted;
        dataUrl = await new Promise<string>((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result as string);
          r.onerror = rej;
          r.readAsDataURL(blob);
        });
      } else {
        dataUrl = await new Promise<string>((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result as string);
          r.onerror = rej;
          r.readAsDataURL(file);
        });
      }
      const data = await apiPost<any>('/api/klassenbuch/foto',
        { bild: dataUrl, klasse_id: aktiveKlasse.klasse_id, datum });
      setFotoVorschau(data.vorschau.stunden);
      setShowFotoModal(false);
    } catch (e) {
      setFotoFehler(e instanceof Error ? e.message : 'Hochladen fehlgeschlagen.');
    } finally {
      setFotoLaden(false);
    }
  }

  async function fotoUebernehmen() {
    if (!aktiveKlasse || !fotoVorschau) return;
    setFotoLaden(true);
    try {
      const data = await apiPost<any>('/api/klassenbuch/foto/uebernehmen',
        { klasse_id: aktiveKlasse.klasse_id, datum, stunden: fotoVorschau });
      toast(`${data.gespeichert} Stunde(n) gespeichert.`, 'success');
      setFotoVorschau(null);
      reloadEintraege();
    } catch (e) {
      zeigeApiFehler(e, 'Speichern fehlgeschlagen');
    } finally {
      setFotoLaden(false);
    }
  }

  function klassenbuchSuchen(q: string) {
    if (!q.trim()) { setSuchTreffer([]); return; }
    setSuchLaden(true);
    const params = new URLSearchParams({ q });
    if (aktiveKlasse) params.set('klasse_id', String(aktiveKlasse.klasse_id));
    fetch(`${API_BASE}/api/klassenbuch/suche?${params}`, { headers: apiHeaders() })
      .then(leseFallback<{ treffer: any[] }>({ treffer: [] }, '/api/klassenbuch/suche'))
      .then(d => setSuchTreffer(d.treffer ?? []))
      .catch(() => setSuchTreffer([]))
      .finally(() => setSuchLaden(false));
  }

  async function startKbDiktat() {
    let zielStunde: number;
    try {
      const sp = await fetch(`${API_BASE}/api/stundenplan/jetzt`, { headers: apiHeaders() }).then(leseFallback<any>(null, '/api/stundenplan/jetzt'));
      // Welle U8: eingebettet nur die Stunde DIESER Klasse übernehmen, nie die Klasse wechseln
      if (sp?.stunde && (!eingebettet || Number(sp.klasse_id) === Number(vorgabeKlasseId))) {
        zielStunde = sp.stunde;
        if (!eingebettet && sp.klasse_id && meineKlassen.length > 0) {
          const passendeKlasse = meineKlassen.find(k =>
            k.klasse_id === sp.klasse_id && k.fach === sp.fach
          ) || meineKlassen.find(k => k.klasse_id === sp.klasse_id);
          if (passendeKlasse) setAktiveKlasse(passendeKlasse);
        }
      } else {
        const h = new Date().getHours();
        zielStunde = h < 8 ? 1 : h < 9 ? 1 : h < 10 ? 2 : h < 11 ? 3 : h < 12 ? 4 : h < 13 ? 5 : h < 14 ? 6 : 7;
      }
    } catch {
      const h = new Date().getHours();
      zielStunde = h < 8 ? 1 : h < 9 ? 1 : h < 10 ? 2 : h < 11 ? 3 : h < 12 ? 4 : h < 13 ? 5 : h < 14 ? 6 : 7;
    }

    let stream: MediaStream;
    try {
      const pending = kbStreamRef.current;
      kbStreamRef.current = null;
      stream = pending ? await pending : await mikrofonAnfragen();
    } catch (e: any) {
      zeigeMikrofonFehler(e);
      return;
    }

    const kandidaten = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
    const mime = kandidaten.find(m => MediaRecorder.isTypeSupported(m)) ?? '';
    let recorder: MediaRecorder;
    try { recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream); }
    catch { recorder = new MediaRecorder(stream); }

    const chunks: Blob[] = [];
    recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };
    recorder.onstop = async () => {
      stream.getTracks().forEach(t => t.stop());
      setKbDiktatPhase('verarbeitung');
      let rohtext = '';
      try {
        const blob = new Blob(chunks, { type: recorder.mimeType || mime || 'audio/webm' });
        const form = new FormData();
        form.append('audio', blob, 'aufnahme.webm');
        const tRes = await fetch(`${API_BASE}/api/transkribiere`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${holeToken() ?? ''}` },
          body: form,
        });
        if (!tRes.ok) throw new Error('transkription');
        const { text } = await tRes.json();
        rohtext = text;
        const felder = await apiPost<any>('/api/klassenbuch/autofill',
          { text, schuelerNamen: klassenSchueler.map(s => s.name) });
        // Merge AI-erkannte Abwesende mit heute vorhandenen Fehlzeiten (M7b)
        const heute = new Date().toISOString().split('T')[0];
        let heuteAbwesend: string[] = [];
        try {
          const fzRows: any[] = await fetch(`${API_BASE}/api/fehlzeiten`, { headers: apiHeaders() })
            .then(leseFallback<any[]>([], '/api/fehlzeiten'));
          const klassenIdArr = klassenSchueler.map(s => Number(s.id));
          heuteAbwesend = fzRows
            .filter(fz => fz.datum === heute && klassenIdArr.includes(fz.schueler_id))
            .map(fz => `${fz.vorname} ${fz.nachname}`);
        } catch {}
        const seen = new Set<string>();
        const alleAbwesend = [...(felder.abwesende_schueler ?? []), ...heuteAbwesend].filter(n => { if (seen.has(n)) return false; seen.add(n); return true; });
        setKbVorausgefuellt({ thema: felder.thema, hausaufgaben: felder.hausaufgaben, anmerkungen: felder.anmerkungen, abwesende_schueler: alleAbwesend });
        toast('Felder vorgeschlagen, bitte prüfen');
      } catch (e: any) {
        if ((e?.message ?? '') === 'transkription') {
          toast('Transkription fehlgeschlagen, bitte von Hand ausfüllen', 'error');
          setKbVorausgefuellt(null);
        } else {
          toast('Strukturierung fehlgeschlagen, Rohtext steht in der Anmerkung', 'error');
          setKbVorausgefuellt(rohtext ? { anmerkungen: rohtext } : null);
        }
      } finally {
        setKbDiktatPhase('idle');
        setOffeneStunde(zielStunde);
      }
    };

    kbDiktatRecorderRef.current = recorder;
    recorder.start();
    setKbDiktatPhase('aufnahme');
  }

  // FAB-Handler: startet direkt Diktat-Aufnahme (iOS: Stream im Gesture-Kontext pre-acquiren)
  const { setFabHandler } = useFab();
  useEffect(() => {
    if (!active) return;
    setFabHandler(() => {
      kbStreamRef.current = mikrofonAnfragen();
      void startKbDiktat();
    });
    return () => { setFabHandler(null); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Fächer der aktiven Klasse — bei keiner Auswahl: alle eigenen Fächer
  const faecher = aktiveKlasse
    ? meineKlassen.filter(k => k.klasse_id === aktiveKlasse.klasse_id).map(k => k.fach).filter(Boolean)
    : Array.from(new Set(meineKlassen.map(k => k.fach))).filter(Boolean);

  // Schüler der aktiven Klasse
  const klassenSchueler = aktiveKlasse
    ? schuelerListe.filter(s => Number(s.klasse_id) === Number(aktiveKlasse.klasse_id))
    : schuelerListe;

  const eintraegeAm = (d: string) => woche[d] ?? [];
  const eintragAm = (d: string, stunde: number): KlassenbuchEintrag | null => eintraegeAm(d).find(e => e.stunde === stunde) ?? null;

  // Fach pro Stunde aus dem vollen Stundenplan (Wochentag des Datums + Stunde + aktive Klasse)
  const planFachAm = (d: string, stunde: number): string | null => {
    const ent = stundenplan.find(e => e.wochentag === wtNr(d) && e.stunde === stunde && (aktiveKlasse ? Number(e.klasse_id) === Number(aktiveKlasse.klasse_id) : true));
    return ent?.fach ?? null;
  };
  // Welle K2: eigene Stunden der Klasse an einem Tag (Stundenplan, ab dem Tag seiner Anlage wie auf dem Server)
  // und dazu jede schon eingetragene Stunde (auch Vertretung)
  const eigeneStunden = (d: string): number[] => {
    if (!aktiveKlasse || wtNr(d) > 5) return [];
    return Array.from(new Set(stundenplan.filter(e => e.wochentag === wtNr(d) && Number(e.klasse_id) === Number(aktiveKlasse.klasse_id)
      && (!e.created_at || String(e.created_at).slice(0, 10) <= d)).map(e => e.stunde))).sort((a, b) => a - b);
  };
  const stundenAm = (d: string): number[] => Array.from(new Set([...eigeneStunden(d), ...eintraegeAm(d).map(e => e.stunde)])).sort((a, b) => a - b);
  const zeitVon = (stunde: number) => zeiten[stunde - 1];
  const zeitText = (stunde: number) => { const z = zeitVon(stunde); return z ? `${fmtZeit(z[0])} bis ${fmtZeit(z[1])}` : null; };
  const jetztMin = new Date().getHours() * 60 + new Date().getMinutes();
  const statusVon = (d: string, stunde: number): Status => {
    if (eintragAm(d, stunde)) return 'eingetragen';
    if (d < heute) return 'offen';
    const z = zeitVon(stunde);
    return d === heute && z && z[1] <= jetztMin ? 'offen' : 'kommend';
  };

  const ichId = getAktuellerNutzer()?.id;
  function nachSpeichern(neu: KlassenbuchEintrag, d = datum) {
    setWoche(prev => ({ ...prev, [d]: [...(prev[d] ?? []).filter(e => e.stunde !== neu.stunde), neu].sort((a, b) => a.stunde - b.stunde) }));
    setOffeneStunde(null);
    setKbVorausgefuellt(null);
    ladeOffen();
  }

  // Welle K2: eine Stunde wählen (auch an einem anderen Tag) — geöffnet wird erst, wenn die Einträge da sind
  function waehle(d: string, stunde: number) {
    if (d === datum && geladenFuer === `${aktiveKlasse?.klasse_id}|${datum}`) { setOffeneStunde(stunde); return; }
    zielStundeRef.current = stunde;
    setDatum(d);
  }

  function datumVerschieben(tage: number) {
    let d = plusTage(datum, tage);
    while (wtNr(d) > 5) d = plusTage(d, tage > 0 ? 1 : -1);   // Wochenende überspringen
    setDatum(d);
  }

  useEffect(() => {
    setOffeneStunde(null);
    setKbVorausgefuellt(null);
  }, [datum]); // eslint-disable-line react-hooks/exhaustive-deps

  // Welle U6b: Sprungziel anwenden, sobald Datum gesetzt und Klassen/Schüler geladen sind
  // (nach dem Datums-Reset oben, damit die Ziel-Stunde offen bleibt)
  useEffect(() => {
    if (!kbZiel || datum !== kbZiel.datum || meineKlassen.length === 0) return;
    if (kbZiel.klasseId == null && kbZiel.schuelerId && schuelerListe.length === 0) return; // Liste lädt noch
    const kid = kbZiel.klasseId ?? (kbZiel.schuelerId ? schuelerListe.find(s => s.id === kbZiel.schuelerId)?.klasse_id : null);
    const k = kid != null ? meineKlassen.find(k => Number(k.klasse_id) === Number(kid)) : null;
    if (k) setAktiveKlasse(k);
    zielStundeRef.current = kbZiel.stunde ?? null;
    setKbZiel(null);
  }, [kbZiel, datum, meineKlassen, schuelerListe]);
  // Stunde erst öffnen, wenn die Einträge GENAU dieser Klasse + dieses Datums da sind —
  // sonst startet der Editor leer und Speichern überschriebe den bestehenden Eintrag.
  useEffect(() => {
    if (zielStundeRef.current == null || !aktiveKlasse || geladenFuer !== `${aktiveKlasse.klasse_id}|${datum}`) return;
    setOffeneStunde(zielStundeRef.current);
    zielStundeRef.current = null;
  }, [geladenFuer, aktiveKlasse, datum]);

  // Welle K2: offene Stunden dieser Klasse (Quelle wie „Jetzt nachtragen“), älteste zuerst
  const offeneDerKlasse = (offen?.eintraege ?? []).filter(e => aktiveKlasse && Number(e.klasse_id) === Number(aktiveKlasse.klasse_id))
    .sort((a, b) => a.datum.localeCompare(b.datum) || a.stunde_nr - b.stunde_nr);
  const offenSeit = offeneDerKlasse.length === 0 ? '' : offeneDerKlasse[0].datum >= montagVon(heute)
    ? (offeneDerKlasse[0].datum === montagVon(heute) ? 'seit Montag' : `seit ${WT_LANG[wtNr(offeneDerKlasse[0].datum)]}`)
    : `seit ${wtKommaTm(offeneDerKlasse[0].datum)}`;
  const offenText = offeneDerKlasse.length > 0
    ? `${offeneDerKlasse.length}${offen && offen.eintraege.length < offen.gesamt ? ' oder mehr' : ''} offene ${offeneDerKlasse.length === 1 ? 'Stunde' : 'Stunden'} ${offenSeit}` : '';

  // PC: die Woche des Datums; gewählt ist (datum, offeneStunde)
  const wochenTage = [0, 1, 2, 3, 4].map(i => plusTage(montagVon(datum), i));
  const wocheGeladen = geladenFuer === `${aktiveKlasse?.klasse_id}|${datum}`;
  // PC: einmal je Woche und Klasse die älteste offene Stunde der Woche vorwählen (sonst heute die erste)
  const autoWahlRef = useRef('');
  useEffect(() => {
    if (isMobile || !aktiveKlasse || !wocheGeladen || offeneStunde !== null || zielStundeRef.current != null) return;
    const schl = `${aktiveKlasse.klasse_id}|${montagVon(datum)}`;
    if (autoWahlRef.current === schl) return;
    autoWahlRef.current = schl;
    for (const d of wochenTage) for (const s of stundenAm(d)) if (statusVon(d, s) === 'offen') { waehle(d, s); return; }
    const heuteStunden = wochenTage.includes(heute) ? stundenAm(heute) : [];
    if (heuteStunden.length) waehle(heute, heuteStunden[0]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, wocheGeladen, aktiveKlasse, datum, offeneStunde]);

  // „Speichern und nächste offene Stunde“: nächste offene in der Woche, sonst die älteste aus der Quelle von Heute
  function naechsteOffene(d: string, stunde: number) {
    for (const t of wochenTage) for (const s of stundenAm(t)) {
      if ((t > d || (t === d && s > stunde)) && statusVon(t, s) === 'offen' && !(t === d && s === stunde)) return { d: t, s };
    }
    const rest = offeneDerKlasse.find(o => !(o.datum === d && o.stunde_nr === stunde));
    return rest ? { d: rest.datum, s: rest.stunde_nr } : null;
  }
  function gespeichert(neu: KlassenbuchEintrag, art: SpeichernArt, d: string, stunde: number) {
    nachSpeichern(neu, d);
    if (art !== 'weiter') return;
    const n = naechsteOffene(d, stunde);
    if (n) waehle(n.d, n.s); else toast('Keine offene Stunde mehr.', 'success');
  }

  const editor = (inline: boolean) => offeneStunde !== null && aktiveKlasse ? (
    <StundeEintragen
      key={`${aktiveKlasse.klasse_id}|${datum}|${offeneStunde}`}
      inline={inline}
      stunde={offeneStunde}
      datum={datum}
      klasse_id={aktiveKlasse.klasse_id}
      klasseName={aktiveKlasse.klasse_name}
      zeit={zeitText(offeneStunde)}
      eintrag={eintragAm(datum, offeneStunde)}
      schuelerListe={klassenSchueler}
      faecher={faecher}
      mitWeiter={inline}
      onSave={(e, art) => gespeichert(e, art, datum, offeneStunde)}
      onGeloescht={id => { setWoche(prev => ({ ...prev, [datum]: (prev[datum] ?? []).filter(x => x.id !== id) })); setOffeneStunde(null); setKbVorausgefuellt(null); ladeOffen(); }}
      onClose={() => { setOffeneStunde(null); setKbVorausgefuellt(null); }}
      vorausgefuellt={kbVorausgefuellt ?? undefined}
      fachVorschlag={planFachAm(datum, offeneStunde)}
    />
  ) : null;

  const ersteFreie = (d: string) => [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].find(s => !eintragAm(d, s) && !eigeneStunden(d).includes(s)) ?? 1;
  const fehlendText = (e: KlassenbuchEintrag) => {
    const ids: number[] = JSON.parse(e.fehlende_schueler);
    const entsch: number[] = JSON.parse(e.entschuldigte_schueler);
    return ids.map(id => ({ s: klassenSchueler.find(s => s.id === id), entsch: entsch.includes(id) })).filter(x => x.s) as { s: { name: string }; entsch: boolean }[];
  };

  // Suche (Thema, Hausaufgabe, Anmerkung)
  const suche = (
    <div className="ld-kt-suche">
      <MIcon name="search" size={19} />
      <input type="search" placeholder="Thema, Hausaufgabe oder Anmerkung suchen" value={suchanfrage} aria-label="Im Klassenbuch suchen"
        onChange={e => { setSuchanfrage(e.target.value); klassenbuchSuchen(e.target.value); }} autoFocus />
      {suchanfrage && (
        <div className="ld-kl-karte ld-kt-treffer">
          {suchLaden ? <div className="ld-kt-leer">Sucht …</div> : suchTreffer.length === 0 ? <div className="ld-kt-leer">Keine Ergebnisse.</div> : suchTreffer.map((t, i) => (
            <button key={i} type="button" onClick={() => { setDatum(t.datum); setSuchanfrage(''); setSuchTreffer([]); setSucheOffen(false); }}>
              <small>{datumTTMMJJJJ(t.datum)}, {t.stunde}. Stunde{t.klasse_name ? `, ${t.klasse_name}` : ''}{t.fach ? `, ${t.fach}` : ''}</small>
              {t.thema || t.hausaufgaben || t.anmerkungen}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // Export: Zeitraum wählen (Felder wie bisher)
  const exportInhalt = (
    <div>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ld-text-primary)' }}>Klassenbuch als PDF</div>
      <div className="ld-kw-export__felder">
        <LdDatum value={exportVon} onChange={e => setExportVon(e.target.value)} aria-label="Von" />
        <span style={{ color: 'var(--ld-text-secondary)' }}>bis</span>
        <LdDatum value={exportBis} onChange={e => setExportBis(e.target.value)} aria-label="Bis" />
      </div>
      <button type="button" className="ld-kl-haupt" style={{ width: '100%' }} onClick={exportKlassenbuch} disabled={exportLaden}>
        <MIcon name="download" size={18} />{exportLaden ? 'Wird erstellt …' : 'PDF herunterladen'}
      </button>
    </div>
  );

  const herkunft = kbHerkunft && (
    <button onClick={zurueckZurHerkunft} className="ld-kb-herkunft">
      <MIcon name="arrow_back" size={16} />Zurück zum Profil
    </button>
  );
  const kopf = !eingebettet && (
    <KlassenKopf kompakt bereich="Klassenbuch" klassen={eindeutigeKlassen(meineKlassen)} aktiveKlasse={aktiveKlasse}
      onWechsel={k => setAktiveKlasse(k)} />
  );

  // ── Handy: ein Tag ──
  const tagAnsicht = () => {
    const stunden = stundenAm(datum);
    const eigene = eigeneStunden(datum);
    const fertig = eigene.filter(s => eintragAm(datum, s)).length;
    return (
      <>
        <div className="ld-kt-tag">
          <button type="button" className="ld-kt-pfeil" aria-label="Voriger Schultag" onClick={() => datumVerschieben(-1)}><MIcon name="chevron_left" size={22} /></button>
          <div className="ld-kt-mitte">
            <button ref={datumKnopf} type="button" className="ld-kt-titel" title="Tag wählen" aria-haspopup="dialog" aria-expanded={kalenderOffen}
              onClick={() => setKalenderOffen(o => !o)}>{tagTitel(datum)}</button>
            {kalenderOffen && <LdKalender anker={datumKnopf.current} wert={datum} onWahl={d => d && setDatum(d)} onClose={() => setKalenderOffen(false)} />}
            {datum === heute
              ? <span className="ld-kt-heute">Heute</span>
              : <button type="button" className="ld-kt-heute" onClick={() => setDatum(heute)}>Zu heute</button>}
            <div className="ld-kt-sub">{eigene.length === 0 ? 'Keine eigenen Stunden' : `${fertig} von ${eigene.length} ${eigene.length === 1 ? 'Stunde' : 'Stunden'} eingetragen`}</div>
          </div>
          <button type="button" className="ld-kt-pfeil" aria-label="Nächster Schultag" onClick={() => datumVerschieben(1)}><MIcon name="chevron_right" size={22} /></button>
        </div>

        {laden && !wocheGeladen ? <LoadingBlock text="Klassenbuch wird geladen …" />
          : stunden.length === 0 ? <div className="ld-kl-karte ld-kt-leer">An diesem Tag hast du in dieser Klasse keinen Unterricht.</div>
          : stunden.map(s => {
            const e = eintragAm(datum, s);
            const st = statusVon(datum, s);
            const z = zeitVon(s);
            const fach = e?.fach ?? planFachAm(datum, s) ?? 'Stunde';
            const fremd = e && e.lehrer_id != null && ichId != null && String(e.lehrer_id) !== String(ichId);
            return (
              <div key={s} className={`ld-kl-karte ld-kt-karte${e ? ' ld-kt-karte--klick' : ''}`} onClick={e ? () => setOffeneStunde(s) : undefined}
                role={e ? 'button' : undefined} tabIndex={e ? 0 : undefined} onKeyDown={e ? (ev => { if (ev.key === 'Enter') setOffeneStunde(s); }) : undefined}
                aria-label={e ? `${fach}, ${s}. Stunde bearbeiten` : undefined}>
                <div className="ld-kt-karte__kopf">
                  <span className="ld-kt-nr"><b>{s}.</b>{z && <small>{fmtZeit(z[0])}</small>}</span>
                  <span className="ld-kt-fach"><b>{fach}</b><small>{zeitText(s) ?? `${s}. Stunde`}</small></span>
                  <span className={`ld-kt-status ld-kt-status--${st}`}>{st === 'eingetragen' && <MIcon name="check" size={15} />}{st}</span>
                </div>
                {e ? (
                  <dl className="ld-kt-zeilen">
                    {e.thema && <><dt>Thema</dt><dd>{e.thema}</dd></>}
                    {e.hausaufgaben && <><dt>Hausaufgabe</dt><dd>{e.hausaufgaben}</dd></>}
                    {e.anmerkungen && <><dt>Anmerkung</dt><dd>{e.anmerkungen}</dd></>}
                    {fehlendText(e).length > 0 && <><dt>Fehlend</dt><dd>{fehlendText(e).map((f, i) => (
                      <span key={i} className={f.entsch ? undefined : 'ld-kt-amber'}>{i > 0 ? '; ' : ''}{f.s.name}, {f.entsch ? 'entschuldigt' : 'unentschuldigt'}</span>
                    ))}</dd></>}
                    {aktiveKlasse && e.fach && <MitarbeitAnzahl klasseId={aktiveKlasse.klasse_id} fach={e.fach} datum={datum} />}
                    {fremd && <><dt>Eingetragen</dt><dd>von {e.lehrer_vorname?.[0]}. {e.lehrer_nachname}</dd></>}
                  </dl>
                ) : st === 'offen' ? (
                  <button type="button" className="ld-kl-haupt ld-kt-knopf" onClick={() => setOffeneStunde(s)}><MIcon name="edit" size={18} />Stunde eintragen</button>
                ) : (
                  <button type="button" className="ld-kl-link" style={{ margin: '8px 0 0 58px' }} onClick={() => setOffeneStunde(s)}>Schon vorbereiten</button>
                )}
              </div>
            );
          })}

        {offeneDerKlasse.length > 0 && (
          <>
            <button type="button" className="ld-kt-offen" onClick={() => waehle(offeneDerKlasse[0].datum, offeneDerKlasse[0].stunde_nr)}>
              <MIcon name="schedule" size={20} /><span>{offenText}</span><b>Nachtragen</b>
            </button>
            <button type="button" className="ld-kl-zweit" style={{ width: '100%', minHeight: 50 }} onClick={() => oeffneKlassenbuchNachtragen()}>
              <MIcon name="mic" size={19} />Per Sprache nachtragen
            </button>
          </>
        )}
        <div className="ld-kt-leise">
          {offeneDerKlasse.length === 0 && <button type="button" className="ld-kl-link" onClick={() => oeffneKlassenbuchNachtragen()}><MIcon name="mic" size={17} />Per Sprache nachtragen</button>}
          <button type="button" className="ld-kl-link" onClick={() => setOffeneStunde(ersteFreie(datum))}><MIcon name="add" size={17} />Weitere Stunde</button>
          <button type="button" className="ld-kl-link" aria-expanded={sucheOffen} onClick={() => setSucheOffen(o => !o)}><MIcon name="search" size={17} />Suchen</button>
          {aktiveKlasse && <button type="button" className="ld-kl-link" onClick={() => { setShowFotoModal(true); setFotoFehler(null); }}><MIcon name="photo_camera" size={17} />Foto vom Papier</button>}
          {aktiveKlasse && <button type="button" className="ld-kl-link" onClick={() => setExportOffen(true)}><MIcon name="print" size={17} />Exportieren</button>}
        </div>
        {sucheOffen && suche}
        <LdSheet open={exportOffen} onClose={() => setExportOffen(false)} kopf={{ titel: 'Klassenbuch exportieren' }} maxWidth={480}>
          <div>{exportInhalt}</div>
        </LdSheet>
        {editor(false)}
      </>
    );
  };

  // ── PC: Woche links, gewählte Stunde rechts ──
  const wochenAnsicht = () => {
    const mo = montagVon(datum), fr = plusTage(mo, 4);
    const kwText = `${Number(mo.slice(8, 10))}. bis ${Number(fr.slice(8, 10))}. ${MONATE[Number(fr.slice(5, 7)) - 1]}`;
    const statusZeichen = (st: Status) => st === 'eingetragen' ? <MIcon name="check_circle" size={21} className="ld-kw-ok" label="eingetragen" />
      : st === 'offen' ? <span className="ld-kw-offen">offen</span> : <span className="ld-kw-kommend">kommend</span>;
    return (
      <>
        <div className="ld-kw-werkzeug">
          <div className="ld-kw-kw">
            <button type="button" className="ld-kt-pfeil" aria-label="Vorige Woche" onClick={() => setDatum(plusTage(mo, -7))}><MIcon name="chevron_left" size={22} /></button>
            <span className="ld-kw-kw__text"><b>KW {kalenderwoche(mo)}</b>{kwText}</span>
            <button type="button" className="ld-kt-pfeil" aria-label="Nächste Woche" onClick={() => setDatum(plusTage(mo, 7))}><MIcon name="chevron_right" size={22} /></button>
          </div>
          {!wochenTage.includes(heute) && <button type="button" className="ld-kl-link" onClick={() => setDatum(heute)}>Zu heute</button>}
          <div className="ld-kw-werkzeug__rechts">
            <button type="button" className="ld-kl-zweit" aria-expanded={sucheOffen} onClick={() => setSucheOffen(o => !o)}><MIcon name="search" size={18} />Suchen</button>
            {aktiveKlasse && <button type="button" className="ld-kl-zweit" onClick={() => { setShowFotoModal(true); setFotoFehler(null); }}><MIcon name="photo_camera" size={18} />Foto vom Papier</button>}
            {aktiveKlasse && <button ref={exportKnopf} type="button" className="ld-kl-zweit" aria-expanded={exportOffen} onClick={() => setExportOffen(o => !o)}><MIcon name="print" size={18} />Exportieren</button>}
            <button type="button" className="ld-kl-zweit" onClick={() => oeffneKlassenbuchNachtragen()}><MIcon name="mic" size={18} />Per Sprache nachtragen</button>
          </div>
        </div>
        {exportOffen && (
          <PortalMenu anker={exportKnopf.current} onClose={() => setExportOffen(false)} label="Klassenbuch exportieren">
            <div style={{ padding: 10, minWidth: 300 }}>{exportInhalt}</div>
          </PortalMenu>
        )}
        {sucheOffen && <div style={{ marginBottom: 14 }}>{suche}</div>}
        {offeneDerKlasse.length > 0 && offeneDerKlasse.some(o => o.datum < mo) && (
          <button type="button" className="ld-kt-offen" onClick={() => waehle(offeneDerKlasse[0].datum, offeneDerKlasse[0].stunde_nr)}>
            <MIcon name="schedule" size={20} /><span>{offenText}</span><b>Nachtragen</b>
          </button>
        )}

        <div className="ld-kw">
          <div className="ld-kl-karte" aria-label="Stunden der Woche">
            {!aktiveKlasse ? <div className="ld-kw-leer">Keine Klasse ausgewählt.</div> : wochenTage.map(d => {
              const stunden = stundenAm(d);
              return (
                <React.Fragment key={d}>
                  <div className="ld-kw-tag">{tagTitel(d)}{d === heute && <span className="ld-kt-heute">Heute</span>}</div>
                  {stunden.length === 0 && <div className="ld-kw-zeile" style={{ cursor: 'default', color: 'var(--ld-text-tertiary)', minHeight: 44 }}>Kein eigener Unterricht</div>}
                  {stunden.map(s => {
                    const e = eintragAm(d, s);
                    const st = statusVon(d, s);
                    const gewaehlt = d === datum && s === offeneStunde;
                    const unter = e ? (e.thema || 'ohne Thema') : gewaehlt ? 'wird gerade eingetragen' : st === 'kommend' ? (d === plusTage(heute, 1) ? 'morgen' : d === heute ? 'heute' : 'noch nicht dran') : 'noch kein Eintrag';
                    return (
                      <button key={s} type="button" className={`ld-kw-zeile${gewaehlt ? ' ist-gewaehlt' : ''}`} aria-current={gewaehlt ? 'true' : undefined} onClick={() => waehle(d, s)}>
                        <span className="ld-kw-nr">{s}.</span>
                        <span className="ld-kw-text"><b>{e?.fach ?? planFachAm(d, s) ?? 'Stunde'}</b><small>{unter}</small></span>
                        {statusZeichen(st)}
                      </button>
                    );
                  })}
                </React.Fragment>
              );
            })}
            {aktiveKlasse && (
              <button type="button" className="ld-kw-weitere" onClick={() => waehle(datum, ersteFreie(datum))}>
                <MIcon name="add" size={18} />Weitere Stunde, z. B. Vertretung
              </button>
            )}
          </div>
          <div className="ld-kl-karte ld-kw-rechts">
            {offeneStunde !== null && aktiveKlasse
              ? editor(true)
              : <div className="ld-kw-leer">{laden && !wocheGeladen ? 'Klassenbuch wird geladen …' : 'Wähle links eine Stunde.'}</div>}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="ld-page-enter">
      {/* Welle U6b: Rückweg ins Profil, wenn von dort gesprungen wurde */}
      {herkunft}
      {/* Welle K1: eigenständig (Seitenleiste) mit dem Klassenkopf; eingebettet steht er schon oben */}
      {kopf}
      {isMobile ? tagAnsicht() : wochenAnsicht()}

      {/* Foto-Upload-Modal */}
      <LdSheet open={showFotoModal} onClose={() => setShowFotoModal(false)} maxWidth={440} kopf={{ titel: 'Foto vom Klassenbuch', icon: 'photo_camera' }}>
            <p style={{ fontSize: 13, color: 'var(--ld-text-secondary)', margin: 0 }}>
              Fotografiere das Klassenbuch aus Papier. Die KI erkennt Stunden, Themen und fehlende Schüler.
            </p>
            {fotoFehler && (
              <div style={{ background: 'var(--ld-danger-soft)', color: 'var(--ld-danger-text)', borderRadius: 'var(--ld-radius-md)', padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>
                {fotoFehler}
              </div>
            )}
            <label style={{ display: 'block', padding: '14px 0', textAlign: 'center', borderRadius: 'var(--ld-radius-md)', border: '2px dashed var(--ld-border-subtle)', cursor: fotoLaden ? 'not-allowed' : 'pointer', background: 'var(--ld-bg-muted)', opacity: fotoLaden ? 0.6 : 1 }}>
              <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                disabled={fotoLaden}
                onChange={e => { const f = e.target.files?.[0]; if (f) fotoHochladen(f); e.target.value = ''; }} />
              {fotoLaden ? (
                <span style={{ color: 'var(--ld-accent)', fontWeight: 600, fontSize: 14 }}><MIcon name="hourglass_top" size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />Wird analysiert …</span>
              ) : (
                <span style={{ color: 'var(--ld-accent)', fontWeight: 600, fontSize: 14 }}><MIcon name="photo_camera" size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />Foto aufnehmen oder auswählen</span>
              )}
            </label>
      </LdSheet>

      {/* Foto-Vorschau-Modal */}
      <LdSheet open={!!fotoVorschau} onClose={() => setFotoVorschau(null)} maxWidth={600} kopf={{ titel: 'KI-Erkennung prüfen', icon: 'auto_awesome' }}
        fuss={fotoVorschau ? <LdFensterFuss onAbbrechen={() => setFotoVorschau(null)} onHaupt={fotoUebernehmen} laden={fotoLaden} aus={!fotoVorschau.some(s => s.uebernehmen)}
          haupt={`Übernehmen (${fotoVorschau.filter(s => s.uebernehmen).length} ${fotoVorschau.filter(s => s.uebernehmen).length === 1 ? 'Stunde' : 'Stunden'})`} /> : undefined}>
        {fotoVorschau && (<>
            <p style={{ fontSize: 13, color: 'var(--ld-text-secondary)', margin: 0 }}>
              Prüfe die erkannten Einträge und deaktiviere Stunden, die du nicht übernehmen möchtest.
            </p>
            <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {fotoVorschau.map((s, i) => (
                <div key={i} style={{ background: 'var(--ld-bg-muted)', borderRadius: 'var(--ld-radius-lg)', padding: 14, opacity: s.uebernehmen ? 1 : 0.45 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <input type="checkbox" checked={s.uebernehmen} style={{ width: 16, height: 16, cursor: 'pointer' }}
                      onChange={e => setFotoVorschau(prev => prev!.map((x, j) => j === i ? { ...x, uebernehmen: e.target.checked } : x))} />
                    <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--ld-text-primary)' }}>{s.stunde}. Stunde</span>
                    <input value={s.fach ?? ''} placeholder="Fach"
                      onChange={e => setFotoVorschau(prev => prev!.map((x, j) => j === i ? { ...x, fach: e.target.value } : x))}
                      style={{ flex: 1, border: 'none', background: 'var(--ld-bg-input)', borderRadius: 'var(--ld-radius-sm)', padding: '4px 8px', fontSize: 13, color: 'var(--ld-text-primary)', outline: 'none' }} />
                  </div>
                  {([['thema', 'Thema'], ['hausaufgaben', 'Hausaufgabe'], ['anmerkungen', 'Anmerkung']] as const).map(([feld, titel]) => (
                    <div key={feld} style={{ marginBottom: 6 }}>
                      <div style={{ fontSize: 12, color: 'var(--ld-text-secondary)', fontWeight: 600, marginBottom: 3 }}>{titel}</div>
                      <input value={s[feld] ?? ''} placeholder={`${titel} …`}
                        onChange={e => setFotoVorschau(prev => prev!.map((x, j) => j === i ? { ...x, [feld]: e.target.value || null } : x))}
                        style={{ width: '100%', boxSizing: 'border-box', border: 'none', background: 'var(--ld-bg-input)', borderRadius: 'var(--ld-radius-sm)', padding: '6px 8px', fontSize: 13, color: 'var(--ld-text-primary)', outline: 'none' }} />
                    </div>
                  ))}
                  {s.fehlende_schueler.length > 0 && (
                    <div style={{ fontSize: 12, color: 'var(--ld-text-secondary)', marginTop: 4 }}>
                      Fehlend: {s.fehlende_schueler.map(id => {
                        const sc = klassenSchueler.find(x => x.id === id);
                        return sc ? sc.name.split(' ')[0] : `#${id}`;
                      }).join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
        </>)}
      </LdSheet>

      {/* Diktat-Aufnahme-Overlay */}
      {kbDiktatPhase !== 'idle' && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 2000,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20,
        }}>
          <div className="ld-fz-mic" style={{
            width: 80, height: 80, borderRadius: '50%',
            background: kbDiktatPhase === 'aufnahme' ? 'var(--ld-danger)' : 'var(--ld-accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: kbDiktatPhase === 'aufnahme' ? 'micPulse 1.2s ease-in-out infinite' : 'none',
          }}>
            <MIcon name={kbDiktatPhase === 'aufnahme' ? 'mic' : 'hourglass_top'} size={34} color="#fff" />
          </div>
          <div style={{ color: 'white', fontSize: 18, fontWeight: 600, textAlign: 'center' }}>
            {kbDiktatPhase === 'aufnahme' ? 'Aufnahme läuft …' : 'Wird verarbeitet …'}
          </div>
          {kbDiktatPhase === 'aufnahme' && (
            <button onClick={() => kbDiktatRecorderRef.current?.stop()} style={{
              padding: '12px 28px', borderRadius: 'var(--ld-radius-md)', border: 'none',
              background: 'white', color: 'var(--ld-danger)', fontSize: 15, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
            }}><MIcon name="stop" size={18} /> Aufnahme stoppen</button>
          )}
        </div>
      )}
    </div>
  );
}
